import React from 'react';
import PropTypes from 'prop-types';
import usePlayerProfile from '../../hooks/usePlayerProfile';
import { VIEW_MODES } from '../../constants';
import { componentText } from '../../utils/i18n_utils';

const ProfileStat = ({ label, value }) => (
  <div className="lbv2__profile-stat">
    <span className="lbv2__profile-stat-label">{label}</span>
    <span className="lbv2__profile-stat-value">{value}</span>
  </div>
);

ProfileStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const handleManagerProfileClick = (e) => {
  e.preventDefault();
  const iframeContainer = document.getElementById('public_profile_iframe');
  if (!iframeContainer) return;
  const iframe = iframeContainer.querySelector('iframe');
  if (iframe) iframe.src = e.currentTarget.href;
};

const ProfileCard = ({ profile, viewMode, leagueId, isManagerSite, memberCardIdStr, showProfileLink = true }) => {
  const subtitle = profile.club_name || profile.state || profile.country;

  const personalStats = [
    profile.age != null && { label: componentText('player_profiles.age', 'age'), value: profile.age },
    profile.weight && { label: componentText('player_profiles.weight', 'weight'), value: profile.weight },
    profile.height && { label: componentText('player_profiles.height', 'height'), value: profile.height },
  ].filter(Boolean);

  const tournamentStats = [
    profile.this_season && profile.events_played != null && {
      label: componentText('player_profiles.tournaments_in_season', `Tournaments in ${profile.this_season}`, { season: profile.this_season }),
      value: profile.events_played,
    },
    profile.total_wins != null && { label: componentText('player_profiles.won', 'Won'), value: profile.total_wins },
    profile.total_points != null && { label: componentText('player_profiles.points', 'Points'), value: profile.total_points },
  ].filter(Boolean);

  const hasStats = viewMode === VIEW_MODES.WEB && (personalStats.length > 0 || tournamentStats.length > 0);

  return (
    <div className="lbv2__profile-card">
      <div className="lbv2__profile-card-identity">
        {profile.photo && (
          <div className="lbv2__profile-photo">
            <img
              src={profile.photo}
              alt=""
              // Profile photo URLs are presigned and can expire. Chrome draws a
              // silent broken-image glyph; Firefox renders the alt text, which
              // is why an empty alt is paired with hiding the element.
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          </div>
        )}
        <div className={`lbv2__profile-info${!profile.photo ? ' lbv2__profile-info--no-photo' : ''}`}>
          <span className="lbv2__profile-name">{profile.name}</span>
          {subtitle && <span className="lbv2__profile-location">{subtitle}</span>}
        </div>
      </div>

      <div className="lbv2__profile-separator">
        {showProfileLink && leagueId && memberCardIdStr && (
          <a
            href={`/profiles/${memberCardIdStr}?league_id=${leagueId}`}
            className="view_profile"
            onClick={isManagerSite ? handleManagerProfileClick : undefined}
          >
            <i className="fa fa-arrow-right lbv2__profile-arrow" aria-hidden="true" />
          </a>
        )}
        {hasStats && <div className="lbv2__profile-divider-line" />}
      </div>
      {hasStats && (
        <div className="lbv2__profile-stats">
          {personalStats.length > 0 && (
            <div className="lbv2__profile-stats-row">
              {personalStats.map(({ label, value }) => (
                <ProfileStat key={label} label={label} value={value} />
              ))}
            </div>
          )}
          {tournamentStats.length > 0 && (
            <div className="lbv2__profile-stats-row">
              {tournamentStats.map(({ label, value }) => (
                <ProfileStat key={label} label={label} value={value} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ProfileCard.propTypes = {
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  leagueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isManagerSite: PropTypes.bool,
  memberCardIdStr: PropTypes.string,
  showProfileLink: PropTypes.bool,
  profile: PropTypes.shape({
    member_id: PropTypes.number,
    member_id_str: PropTypes.string,
    has_public_profile: PropTypes.bool,
    name: PropTypes.string,
    photo: PropTypes.string,
    club_name: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    age: PropTypes.number,
    weight: PropTypes.string,
    height: PropTypes.string,
    this_season: PropTypes.string,
    events_played: PropTypes.number,
    total_wins: PropTypes.number,
    total_points: PropTypes.number,
  }).isRequired,
};

const PlayerProfiles = ({ memberIds, memberCards = [], leagueId, memberProfilesUrl, viewMode, isManagerSite }) => {
  const { profiles, loading } = usePlayerProfile(memberIds, memberProfilesUrl);

  if (loading || !profiles.length) return null;

  const memberCardIdStrByMemberId = Object.fromEntries(
    memberCards.map((mc) => [mc.memberIdStr, mc.memberCardIdStr])
  );

  return (
    <div className="lbv2__player-profiles">
      {profiles.map((profile) => (
        <ProfileCard
          key={profile.member_id}
          profile={profile}
          viewMode={viewMode}
          leagueId={leagueId}
          isManagerSite={isManagerSite}
          memberCardIdStr={memberCardIdStrByMemberId[profile.member_id_str]}
          showProfileLink={Boolean(profile.has_public_profile)}
        />
      ))}
    </div>
  );
};

PlayerProfiles.propTypes = {
  memberIds: PropTypes.arrayOf(PropTypes.string),
  memberCards: PropTypes.arrayOf(PropTypes.shape({
    memberIdStr: PropTypes.string,
    memberCardIdStr: PropTypes.string,
  })),
  leagueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  memberProfilesUrl: PropTypes.string,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  isManagerSite: PropTypes.bool,
};

export default PlayerProfiles;
